// @flow
import React, { Component, type Node } from 'react';
import styled from 'styled-components';
import { Icon } from 'client-web-app/ui';
import { colors } from '../bench-style-constants';

const ClientWebAppFallbackUILayout = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

const ClientWebAppFallbackUIText = styled.div`
  color: ${colors.charcoal};
  font-size: 16px;
  line-height: 24px;
  max-width: 400px;
  padding-top: 14px;
  text-align: center;
`;

const ClientWebAppFallbackUI = () => (
  <ClientWebAppFallbackUILayout>
    <Icon color={colors.charcoal} size="36px" icon="exclamation" />
    <ClientWebAppFallbackUIText>
      Oops, it looks like something went wrong. We’re working on fixing this issue as quickly as we
      can. If you continue to see this message, please contact your bookkeeper for assistance.
    </ClientWebAppFallbackUIText>
  </ClientWebAppFallbackUILayout>
);

type ClientWebAppErrorBoundaryProps = {
  children: Node,
  showEmptyFallbackUI: boolean,
  clientId: string,
};

type ClientWebAppErrorBoundaryState = {
  hasError: boolean,
};

class ClientWebAppErrorBoundary extends Component<
  ClientWebAppErrorBoundaryProps,
  ClientWebAppErrorBoundaryState,
> {
  state = {
    hasError: false,
  };

  componentDidCatch(error: Object, info: Object) {
    const { clientId } = this.props;
    this.setState({ hasError: true });
    if (window.Rollbar.error) {
      window.Rollbar.error(error, { clientId, ...info });
    }
  }

  render() {
    const { hasError } = this.state;
    const { children, showEmptyFallbackUI } = this.props;
    if (hasError) {
      return showEmptyFallbackUI ? null : <ClientWebAppFallbackUI />;
    }
    return children;
  }
}

export default ClientWebAppErrorBoundary;
